import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateLayouts } from '../utils/layoutGenerator';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Textarea } from './ui/textarea';
import {
    ArrowRight,
    Layout,
    Smartphone,
    Monitor,
    Check,
    Grid,
    Layers,
    ChevronLeft,
    AlertTriangle,
    Sparkles,
    Palette,
    Film,
    Image
} from 'lucide-react';
import { toast } from 'sonner';
import LayoutDesigner from './LayoutDesigner';

// Helper component to visualize a layout option
const LayoutPreview = ({ layout, width, height, selected, onClick }) => {
    // Scale down validation for preview (max width 200px)
    const scale = 200 / width;
    const previewH = height * scale;

    return (
        <div
            className={`cursor-pointer group relative p-2 rounded-xl transition-all duration-300 ${selected ? 'bg-purple-600 ring-2 ring-purple-400' : 'bg-gray-800 hover:bg-gray-750'
                }`}
            onClick={onClick}
        >
            <div
                className="relative bg-white mx-auto shadow-lg transition-transform group-hover:scale-[1.02]"
                style={{ width: 200, height: previewH }}
            >
                {layout.panels.map((panel) => (
                    <div
                        key={panel.id}
                        className={`absolute flex items-center justify-center text-xs text-gray-500 font-mono ${panel.zIndex > 0
                            ? 'border-2 border-white shadow-md z-10'
                            : 'border border-gray-900/10'
                            }`}
                        style={{
                            left: panel.x * scale,
                            top: panel.y * scale,
                            width: panel.width * scale,
                            height: panel.height * scale,
                            backgroundColor: panel.color,
                            zIndex: panel.zIndex || 0,
                            clipPath: panel.clipPath !== 'none' ? panel.clipPath : undefined
                        }}
                    >
                        {panel.id}
                    </div>
                ))}
            </div>
            <div className="mt-3 text-center">
                <p className={`text-sm font-medium ${selected ? 'text-white' : 'text-gray-300'}`}>
                    {layout.name}
                </p>
            </div>

            {selected && (
                <div className="absolute top-2 right-2 bg-white text-purple-600 rounded-full p-1 shadow-md">
                    <Check className="w-4 h-4" />
                </div>
            )}
        </div>
    );
};

const CreateComicFlow = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [showDesigner, setShowDesigner] = useState(false);

    // Project Data State
    const [projectData, setProjectData] = useState({
        title: '',
        description: '',
        story: '',
        artStyle: '',
        characterDescriptions: '',
        aiModel: 'caravaggio', // 'caravaggio' or 'gemini'
        generationMethod: 'image', // 'image' or 'video'
        orientation: 'portrait',
        panelCount: 4,
        selectedLayoutId: null,
        layout: null, // Full layout object
        pageWidth: 1200,
        pageHeight: 1600
    });

    // Generated Layout Options
    const [layoutOptions, setLayoutOptions] = useState([]);

    // step 1 validation
    const canProceedStep1 = projectData.title.trim().length > 0;

    // step 2 effect - update generated layouts when config changes
    useEffect(() => {
        // A4 Standard Ratio approx
        const w = projectData.orientation === 'portrait' ? 1200 : 1600;
        const h = projectData.orientation === 'portrait' ? 1600 : 1200;

        setProjectData(prev => ({ ...prev, pageWidth: w, pageHeight: h }));

        const options = generateLayouts(w, h, projectData.panelCount);
        setLayoutOptions(options);

        // Always re-assign layout from the NEW options so panel coordinates
        // match the new page dimensions (portrait vs landscape).
        // Layout IDs like 'grid-4' are the same for both orientations,
        // but the panel coordinates inside them are different.
        if (projectData.selectedLayoutId === 'custom-drawn') return;

        const matchingNew = options.find(l => l.id === projectData.selectedLayoutId);
        if (matchingNew) {
            // Re-assign even if ID matches — coordinates differ per orientation
            setProjectData(prev => ({
                ...prev,
                layout: matchingNew
            }));
        } else if (options.length > 0) {
            setProjectData(prev => ({
                ...prev,
                selectedLayoutId: options[0].id,
                layout: options[0]
            }));
        }
    }, [projectData.orientation, projectData.panelCount]);

    const handleCreate = () => {
        // In a real implementation, this would save to backend.
        // For now, we'll navigate to the Blueprint View with state
        console.log("Creating Comic with Data:", projectData);
        toast.success("Blueprint Created!");

        // Navigate to Blueprint View (To be implemented)
        // passing state via location
        navigate('/comic-blueprint', { state: { project: projectData } });
    };

    // Handle Custom Layout Save
    const handleCustomLayoutSave = (customPanels) => {
        const customLayout = {
            id: 'custom-drawn',
            name: "Custom Drawn Layout",
            panels: customPanels
        };

        const updatedProjectData = {
            ...projectData,
            selectedLayoutId: 'custom-drawn',
            layout: customLayout
        };

        // Update state (for correctness if we stayed)
        setProjectData(updatedProjectData);
        setShowDesigner(false);
        toast.success("Blueprint Created!");

        // Navigate immediately
        navigate('/comic-blueprint', { state: { project: updatedProjectData } });
    };

    return (
        <div className="min-h-screen bg-gray-950 text-white flex flex-col items-center py-12 px-4 relative">
            {/* Custom Layout Designer Overlay */}
            {showDesigner && (
                <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-8 animate-in fade-in duration-200">
                    <div className="w-full max-w-6xl h-full max-h-[90vh]">
                        <LayoutDesigner
                            width={projectData.pageWidth}
                            height={projectData.pageHeight}
                            onSave={handleCustomLayoutSave}
                            onCancel={() => setShowDesigner(false)}
                        />
                    </div>
                </div>
            )}

            {/* Progress Header */}
            <div className="w-full max-w-4xl mb-8 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-purple-600' : 'bg-gray-800'}`}>1</div>
                    <div className="h-1 w-12 bg-gray-800">
                        <div className={`h-full bg-purple-600 transition-all ${step > 1 ? 'w-full' : 'w-0'}`} />
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-purple-600' : 'bg-gray-800'}`}>2</div>
                    <div className="h-1 w-12 bg-gray-800">
                        <div className={`h-full bg-purple-600 transition-all ${step > 2 ? 'w-full' : 'w-0'}`} />
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 3 ? 'bg-purple-600' : 'bg-gray-800'}`}>3</div>
                </div>

                <div className="text-gray-400 font-medium">
                    {step === 1 && "Project Details"}
                    {step === 2 && "Layout Configuration"}
                    {step === 3 && "Final Review"}
                </div>
            </div>

            <Card className="w-full max-w-4xl bg-gray-900 border-gray-800 shadow-2xl">
                <CardHeader>
                    <CardTitle className="text-2xl text-purple-400">
                        {step === 1 && "Start Your New Comic"}
                        {step === 2 && "Choose Your Layout"}
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                        {step === 1 && "Give your project a name and description."}
                        {step === 2 && "Configure the page structure. You can always change this later."}
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">

                    {/* STEP 1: Details */}
                    {step === 1 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Comic Title</Label>
                                <Input
                                    id="title"
                                    value={projectData.title}
                                    onChange={(e) => setProjectData(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="The Amazing Adventures..."
                                    className="bg-gray-800 border-gray-700 focus:border-purple-500 placeholder:text-gray-600"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="desc">Description (Optional)</Label>
                                <Textarea
                                    id="desc"
                                    value={projectData.description}
                                    onChange={(e) => setProjectData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="A story about..."
                                    className="bg-gray-800 border-gray-700 focus:border-purple-500 placeholder:text-gray-600"
                                />
                            </div>

                            {/* AI Auto-Generation Fields */}
                            <div className="mt-6 p-4 bg-gradient-to-r from-purple-900/30 to-indigo-900/30 rounded-xl border border-purple-500/20">
                                <div className="flex items-center space-x-2 mb-4">
                                    <span className="text-lg">🤖</span>
                                    <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wider">AI Auto-Generation</h3>
                                    <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">Optional</span>
                                </div>

                                {/* Model Selector */}
                                <div className="mb-4">
                                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">AI Model</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setProjectData(prev => ({ ...prev, aiModel: 'caravaggio' }))}
                                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${projectData.aiModel === 'caravaggio'
                                                ? 'bg-gradient-to-r from-amber-600/30 to-orange-600/30 border border-amber-500/50 text-amber-300'
                                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-transparent'
                                                }`}
                                        >
                                            <Palette className="w-4 h-4" />
                                            <div className="text-left">
                                                <span className="block">CARAVAGGIO</span>
                                                <span className="block text-[10px] opacity-60">Custom Model • Fast</span>
                                            </div>
                                            {projectData.aiModel === 'caravaggio' && <Check className="w-4 h-4 ml-auto text-amber-400" />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setProjectData(prev => ({ ...prev, aiModel: 'gemini', generationMethod: 'image' }))}
                                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${projectData.aiModel === 'gemini'
                                                ? 'bg-gradient-to-r from-blue-600/30 to-cyan-600/30 border border-cyan-500/50 text-cyan-300'
                                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-transparent'
                                                }`}
                                        >
                                            <Sparkles className="w-4 h-4" />
                                            <div className="text-left">
                                                <span className="block">Gemini</span>
                                                <span className="block text-[10px] opacity-60">Google AI</span>
                                            </div>
                                            {projectData.aiModel === 'gemini' && <Check className="w-4 h-4 ml-auto text-cyan-400" />}
                                        </button>
                                    </div>
                                    {projectData.aiModel === 'gemini' && (
                                        <div className="mt-2 p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-start gap-2">
                                            <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                                            <p className="text-xs text-yellow-400">
                                                Gemini may be slower in generation and take longer to produce results. For fastest results, use CARAVAGGIO.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Generation Method Selector */}
                                <div className="mb-4">
                                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Generation Method</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setProjectData(prev => ({ ...prev, generationMethod: 'image' }))}
                                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${projectData.generationMethod === 'image'
                                                ? 'bg-gradient-to-r from-purple-600/30 to-pink-600/30 border border-purple-500/50 text-purple-300'
                                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-transparent'
                                                }`}
                                        >
                                            <Image className="w-4 h-4" />
                                            <div className="text-left">
                                                <span className="block">Image per Panel</span>
                                                <span className="block text-[10px] opacity-60">Standard • Independent</span>
                                            </div>
                                            {projectData.generationMethod === 'image' && <Check className="w-4 h-4 ml-auto text-purple-400" />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => projectData.aiModel !== 'gemini' && setProjectData(prev => ({ ...prev, generationMethod: 'video' }))}
                                            disabled={projectData.aiModel === 'gemini'}
                                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${projectData.aiModel === 'gemini'
                                                ? 'bg-gray-800/50 text-gray-500 border border-transparent cursor-not-allowed opacity-50'
                                                : projectData.generationMethod === 'video'
                                                    ? 'bg-gradient-to-r from-emerald-600/30 to-teal-600/30 border border-emerald-500/50 text-emerald-300'
                                                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-transparent'
                                                }`}
                                        >
                                            <Film className="w-4 h-4" />
                                            <div className="text-left">
                                                <span className="block">Video → Panels</span>
                                                <span className="block text-[10px] opacity-60">Consistent • Cinematic</span>
                                            </div>
                                            {projectData.aiModel === 'gemini'
                                                ? <span className="text-[10px] ml-auto text-gray-500 italic">Coming Soon</span>
                                                : projectData.generationMethod === 'video' && <Check className="w-4 h-4 ml-auto text-emerald-400" />}
                                        </button>
                                    </div>
                                    {projectData.generationMethod === 'video' && (
                                        <div className="mt-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                            <p className="text-xs text-emerald-400 font-medium mb-1">
                                                🎬 Caravaggio Video → Comic Panels
                                            </p>
                                            <p className="text-xs text-emerald-400/80">
                                                Generates a video from your story using Caravaggio AI, extracts key frames, then stylizes
                                                each frame into comic art using your Caravaggio art style.
                                                Characters stay consistent across all panels!
                                            </p>
                                            <p className="text-xs text-amber-400 mt-1.5 flex items-center gap-1">
                                                ⏱ Takes approximately <strong>3-5 minutes</strong> — please be patient while
                                                the AI generates and stylizes your panels.
                                            </p>
                                        </div>
                                    )}
                                    {projectData.generationMethod === 'image' && (
                                        <div className="mt-2 p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
                                            <p className="text-xs text-purple-400">
                                                📸 Generates each panel independently. Faster, but characters may vary between panels.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    <div className="space-y-1">
                                        <Label htmlFor="story" className="text-sm text-gray-300">Your Story</Label>
                                        <Textarea
                                            id="story"
                                            value={projectData.story}
                                            onChange={(e) => setProjectData(prev => ({ ...prev, story: e.target.value }))}
                                            placeholder="A brave knight enters a dark cave to confront a fearsome dragon that has been terrorizing the village..."
                                            className="bg-gray-800/50 border-gray-700 focus:border-purple-500 min-h-[80px] placeholder:text-gray-600"
                                            rows={3}
                                        />
                                        <p className="text-xs text-gray-500">Describe your story — AI will break it into panels and pre-fill them with images</p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="space-y-1">
                                            <Label htmlFor="artStyle" className="text-sm text-gray-300">Art Style</Label>
                                            <Input
                                                id="artStyle"
                                                value={projectData.artStyle}
                                                onChange={(e) => setProjectData(prev => ({ ...prev, artStyle: e.target.value }))}
                                                placeholder="e.g., manga, watercolor, noir..."
                                                className="bg-gray-800/50 border-gray-700 focus:border-purple-500 placeholder:text-gray-600"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label htmlFor="characters" className="text-sm text-gray-300">Characters</Label>
                                            <Input
                                                id="characters"
                                                value={projectData.characterDescriptions}
                                                onChange={(e) => setProjectData(prev => ({ ...prev, characterDescriptions: e.target.value }))}
                                                placeholder="e.g., Hero: tall, red cape, short black hair..."
                                                className="bg-gray-800/50 border-gray-700 focus:border-purple-500 placeholder:text-gray-600"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: Configuration & Layout */}
                    {step === 2 && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-right-8">

                            {/* Configuration Controls */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6 bg-gray-800/50 rounded-xl border border-gray-800">

                                {/* Orientation Selector */}
                                <div className="space-y-3">
                                    <Label>Page Orientation</Label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setProjectData(prev => ({ ...prev, orientation: 'portrait' }))}
                                            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${projectData.orientation === 'portrait'
                                                ? 'border-purple-500 bg-purple-500/10 text-white'
                                                : 'border-gray-700 text-gray-400 hover:border-gray-600'
                                                }`}
                                        >
                                            <Smartphone className="w-8 h-8 mb-2" />
                                            <span className="font-medium">Portrait</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setProjectData(prev => ({ ...prev, orientation: 'landscape' }))}
                                            className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${projectData.orientation === 'landscape'
                                                ? 'border-purple-500 bg-purple-500/10 text-white'
                                                : 'border-gray-700 text-gray-400 hover:border-gray-600'
                                                }`}
                                        >
                                            <Monitor className="w-8 h-8 mb-2" />
                                            <span className="font-medium">Landscape</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Panel Count Selector */}
                                <div className="space-y-3">
                                    <Label>Number of Panels: {projectData.panelCount}</Label>
                                    <div className="pt-4 px-2">
                                        <input
                                            type="range"
                                            min="1"
                                            max="9"
                                            value={projectData.panelCount}
                                            onChange={(e) => setProjectData(prev => ({ ...prev, panelCount: parseInt(e.target.value) }))}
                                            className="w-full accent-purple-500 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                        />
                                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                                            <span>1</span>
                                            <span>5</span>
                                            <span>9</span>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-400 text-center">
                                        Drag to adjust panel count
                                    </p>
                                </div>
                            </div>

                            {/* Generated Layouts Grid */}
                            <div className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <Layout className="w-5 h-5 text-purple-400" />
                                    <Label className="text-lg">Select a Blueprint</Label>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                                    {/* Custom Layout Card */}
                                    <div
                                        onClick={() => setShowDesigner(true)}
                                        className={`cursor-pointer group relative p-2 rounded-xl bg-gray-800 hover:bg-gray-750 border-2 border-dashed transition-all ${projectData.selectedLayoutId === 'custom-drawn' ? 'border-purple-500 bg-purple-500/10' : 'border-purple-500/50 hover:border-purple-500'}`}
                                    >
                                        <div className="h-[200px] flex flex-col items-center justify-center text-gray-400 group-hover:text-white transition-colors">
                                            <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mb-3 group-hover:bg-purple-500 group-hover:text-white transition-all">
                                                <Layers className="w-6 h-6" />
                                            </div>
                                            <span className="font-semibold">Create Custom Layout</span>
                                            <span className="text-xs mt-1 text-gray-500">Draw & Skew Panels</span>
                                        </div>
                                        {projectData.selectedLayoutId === 'custom-drawn' && (
                                            <div className="absolute top-2 right-2 bg-white text-purple-600 rounded-full p-1 shadow-md">
                                                <Check className="w-4 h-4" />
                                            </div>
                                        )}
                                    </div>

                                    {layoutOptions.map((layout) => (
                                        <LayoutPreview
                                            key={layout.id}
                                            layout={layout}
                                            width={projectData.pageWidth}
                                            height={projectData.pageHeight}
                                            selected={projectData.selectedLayoutId === layout.id}
                                            onClick={() => setProjectData(prev => ({
                                                ...prev,
                                                selectedLayoutId: layout.id,
                                                layout: layout
                                            }))}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex justify-between pt-6 border-t border-gray-800">
                        {step === 1 ? (
                            <div /> // Spacer
                        ) : (
                            <Button
                                variant="ghost"
                                onClick={() => setStep(step - 1)}
                                className="text-gray-400 hover:text-white"
                            >
                                <ChevronLeft className="w-4 h-4 mr-2" /> Back
                            </Button>
                        )}

                        {step < 2 ? (
                            <Button
                                onClick={() => setStep(step + 1)}
                                disabled={!canProceedStep1}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-8"
                            >
                                Next Step <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <Button
                                onClick={handleCreate}
                                className="bg-green-600 hover:bg-green-700 text-white px-8"
                            >
                                Create Blueprint <Check className="w-4 h-4 ml-2" />
                            </Button>
                        )}
                    </div>

                </CardContent>
            </Card>

            {/* Custom Layout Designer Overlay */}
            {showDesigner && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
                    <div className="w-full max-w-6xl h-[90vh] bg-gray-950 rounded-xl shadow-2xl overflow-hidden border border-gray-800">
                        <LayoutDesigner
                            width={projectData.pageWidth}
                            height={projectData.pageHeight}
                            onSave={handleCustomLayoutSave}
                            onCancel={() => setShowDesigner(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateComicFlow;
